import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ComponentRef,
    createNgModuleRef,
    EventEmitter,
    Injector,
    Input,
    NgZone,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
    ViewContainerRef
} from '@angular/core';
import {ResourceDataService} from '../../../../service/services/rest/resource-data/resource-data.service';
import {firstValueFrom, Subscription} from 'rxjs';
import {Const, ResourceState, ResourceType} from '../../../../core/helpers/Const';
import {Utils} from '../../../../core/helpers/Utils';
import {FlexibleColumnLayout, TabListComponent, TabPanelComponent} from '@fundamental-ngx/core';
import {ResourceModel} from '../../../../core/models/resources/resource.model';
import {DialogService} from '../../../../service/services/misc/dialog/dialog.service';
import {DetailsComponent} from './sections/details/details.component';

import {UisService} from "../../../../service/services/misc/uis/uis.service";
import {SwaService} from "../../../../service/services/misc/swa/swa.service";
import {ResourceModel as ReferenceResourceModel} from "../../../../core/models/misc/references.model";
import {SolutionService} from "../../../../extension/cloud-automation-designer/services/solution.service";
import {JsonEditorComponent} from "../../../../shared/components/json-editor/json-editor.component";
import {AppConfigService} from "../../../../service/services/misc/app-config/app-config.service";
import {NotificationsService} from "../../../../service/services/misc/notifications/notifications.service";
import {FetchResponse, PollingService} from "../../../../service/services/misc/polling";
import {AiAssistantService} from "../../../../service/services/misc/ai-assistant/ai-assistant.service";

@Component({
    selector: 'app-content-details',
    templateUrl: './content-details.component.html',
    styleUrls: ['./content-details.component.scss'],
    standalone: false
})
export class ContentDetailsComponent implements OnChanges, AfterViewInit {
    Utils = Utils;
    subscriptions: Subscription[] = [];
    loadingRTDResources = false;
    hasPrivilegesPermissions = false;
    loadingResource = false;
    rtdErrorDetails: string;
    rtdResourcesStateMessage = false;

    rtdResources: any;

	@Input() resource: ResourceModel;
	@Input() policiesLoading = false;
	@Output() editWithForm: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
	@Output() editOrganization: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
	@Output() onReconcileResource: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
	@Output() delete: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
	@Output() publish: EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();
	@Output() onEditResource: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() onUpdateRTDResource: EventEmitter<boolean> = new EventEmitter<boolean>();
	@Output() reloadResource : EventEmitter<ResourceModel> = new EventEmitter<ResourceModel>();


	showMidColumnControls = false;
	isPoliciesVisible = true;
	isAutomationSolution = false;

	@ViewChild('tabListComponent')
	    tabListComponent: TabListComponent;

	@ViewChild('detailsComponent')
	    detailsComponent: DetailsComponent;

	@ViewChild('policiesTab')
	    policiesTab: TabPanelComponent;

	@ViewChild('relationsTab')
	    relationsTab: TabPanelComponent;

	@ViewChild('relationsComponent', {read: ViewContainerRef})
	    relationsView: ViewContainerRef;

	@ViewChild(JsonEditorComponent)
	    editor : JsonEditorComponent;

	private relationsComponentInstance: any;
	private relationsComponent: ComponentRef<any>;

	@ViewChild('eventsTab')
	    eventsTab: TabPanelComponent;

	@ViewChild('eventsComponent', {read: ViewContainerRef})
	    eventsView: ViewContainerRef;

	@ViewChild('resourcesTab')
	    resourcesTab: TabPanelComponent;

	@ViewChild('rtdResourcesComponent', {read: ViewContainerRef})
	    resourcesView: ViewContainerRef;


	private eventsComponentInstance: any;
	private eventsComponentRef: ComponentRef<any>;

	@ViewChild('automationTab')
	    automationTab: TabPanelComponent;

	@ViewChild('automationComponent', {read: ViewContainerRef})
	    automationView: ViewContainerRef;

	private automationComponentInstance: any;
	private automationComponent: ComponentRef<any>;

  @ViewChild('runtimePolicies')
	  runtimePoliciesTab: TabPanelComponent;


  @ViewChild('detailsTab')
      detailsTab: TabPanelComponent;

  selectedTabId: string;

	@Output() doCloseFcl: EventEmitter<any> = new EventEmitter<any>();
	@Input() localLayout: FlexibleColumnLayout;

	constructor(
		private resourceDataService: ResourceDataService,
		private ref: ChangeDetectorRef,
		private _dialogService: DialogService,
		private _ngZone: NgZone,
		private _uisService: UisService,
		private _swaService: SwaService,
		private injector: Injector,
		private solutionService: SolutionService,
		private _appConfigService: AppConfigService,
		private notificationsService: NotificationsService,
		private pollingService: PollingService,
		private _aiAssistantService: AiAssistantService
	) {
	}

	ngOnChanges(changes: SimpleChanges) {

	    let layout = changes['localLayout'];
	    let resourceChange = changes['resource'];
	    if (layout) {
	        this.onLayoutChange(layout.currentValue);
	    }
	    const resource = resourceChange?.currentValue || this.resource;
	    if (resource && this.localLayout !== 'OneColumnStartFullScreen') {
	        this.onResourceChange(resource);
	    }
	}

	onLayoutChange(newLayout: FlexibleColumnLayout) {
	    this.showMidColumnControls = newLayout.startsWith('Two');
	    if (this.relationsComponentInstance) {
	        (this.relationsComponentInstance as any).closeLegend();
	    }
	}

	async onResourceChange(resource: ResourceModel) {

	    this.rtdResources = undefined;
	    this.isAutomationSolution = resource.type === ResourceType.AUTOMATION_SOLUTION;
	    let invalidSolutionMessage = "";

	    this.setDetails(resource);
	    if (this.relationsTab && this.relationsTab.expanded) {
	        // Only load references if the resource has actually changed (not just polling updates)
	        // Skip reference loading for resources in IN_PROCESS state unless it's the first load
	        const currentRootResourceId = (this.relationsComponentInstance as any)?.rootResourceId;
	        const newResourceId = resource ? resource.uid : null;
	        const isFirstLoad = !currentRootResourceId;
	        const isResourceChanged = currentRootResourceId !== newResourceId;
	        const isInProcess = resource?.state === ResourceState.IN_PROCESS || resource?.state === ResourceState.TERMINATING;

	        if (isFirstLoad || (isResourceChanged && !isInProcess)) {
	            this.loadReferences(resource);
	        }
	    } else if (this.relationsComponentInstance) {
	        (this.relationsComponentInstance as any).closeLegend();
	    }

	    if (this.eventsTab && this.eventsTab.expanded) {
	        this.loadEvents(resource);
	    }

	    if(this.resourcesTab && this.resourcesTab.expanded) {
	        this.loadRTDResources(resource);
	    }

	    if (this.automationTab?.expanded) {
	        if (resource != null) {
	            let invalidSolution = false;
	            try {
	                this.solutionService.loadSolution(resource.resource, true);
	            } catch (error) {
	                console.error(error);
	                invalidSolutionMessage = Utils.enrichErrorMessage(Const.INVALID_CAD_FORMAT_OF_AUTOMATION_SOLUTION + "'" + error + "'");
	                invalidSolution = true;
	            }
	            await this.loadAutomationComponent(invalidSolution, invalidSolutionMessage);
	        }
	    }

	}

	setDetails(resource: ResourceModel) {
	    this.detailsComponent?.initDetails(resource);
	    this.isPoliciesVisible = Utils.showPolicies(resource);
	}

	getResourceDetailsUrl(resource: ResourceModel, fullResourcePath: string, query = '?extended=true') {
	    let url = `${resource.apiVersion}/${resource.type}${fullResourcePath}${query}`;
	    return url;
	}

	async onTabChange(event: TabPanelComponent) {
	    this.selectedTabId = event.id;
		  let invalidSolutionMessage = "";
	    // this function is called on each navigation in the hierarchy - send event only for policies tab
	    if (event.id === 'detailsRuntimePoliciesTab') {
	        this._swaService.sendCustomEvent(this._swaService.SWA_EVENT_TYPES.SELECT_POLICIES_TAB,
	            [this.resource?.type], []);

	        this._uisService.sendUISEvent(this._uisService.UIS_EVENT_TYPES.SELECT_POLICIES_TAB + " - " +
				this.resource?.type, {type: this.resource?.type, path: this.resource?.path});
	    }

	    if (event.id === 'relationsTab') {
	        this.loadReferences(this.resource);
	    } else if (this.relationsComponentInstance) {
	        (this.relationsComponentInstance as any).closeLegend();
	    }

	    if (event.id === 'eventsTab') {
	        this.loadEvents(this.resource);
	    }

	    if (event.id === 'automationTab') {
	        if(this.resource != null) {
	            let invalidSolution = false;
	            try {
	                this.solutionService.loadSolution(this.resource.resource, true);
	            } catch (error) {
	                console.error(error);
	                invalidSolutionMessage = Utils.enrichErrorMessage(Const.INVALID_CAD_FORMAT_OF_AUTOMATION_SOLUTION + "'" + error + "'");
	                invalidSolution = true;
	            }
	            await this.loadAutomationComponent(invalidSolution, invalidSolutionMessage);
	        }
	    }

	    if(event.id === 'resourcesTab') {
	        this.loadRTDResources(this.resource);
	    }

	    this.ref.detectChanges();
	}

	onClose() {
	    this.doCloseFcl.emit();
	}

	openPoliciesTab(resource: any) {
	    this.resource = resource;
	    this.runtimePoliciesTab.open(true);
	}

	openReferencesTab(resource: any) {
	    this.resource = resource;
	    this.relationsTab.open(true);
	}

	openEventsTab(resource: any) {
	    this.resource = resource;
	    this.eventsTab.open(true);
	}

	openRTDResourcesTab(resource: any) {
	    this.resource = resource;
	    this.ref.detectChanges();

	    this.resourcesTab?.open(true);
	}

	onAnalyzeFailure(resource: ResourceModel) {
	    this._aiAssistantService.open(this._aiAssistantService.buildResourceContext(resource));
	}

	get showAnalyzeFailure(): boolean {
	    return Utils.isAiAssistantAvailable()
			&& [ResourceState.FAILED, ResourceState.TERMINATION_FAILED, ResourceState.NOT_READY]
			    .includes(this.resource?.state);
	}

	async onReloadResource() {
	    const resource = this.resource;
	    let path = resource.path || '';
	    if (path === Const.ROOT_PATH) {
	        path = '';
	    }
	    let fullResourcePath = `${path}/${resource.name}`;
	    let url = this.getResourceDetailsUrl(this.resource, fullResourcePath, "?returnType=model");

	    try {
	        this.loadingResource = true;
	        const res = await firstValueFrom(this.resourceDataService.get(url));
	        this.setDetails(res);
	    } catch (e) {

	    } finally {
	        this.loadingResource = false;
	    }
	}

	async loadReferences(resource: ResourceModel) {
	    await this.loadReferencesComponent();
	    await ((this.relationsComponentInstance as any).loadRootResource(
	        ReferenceResourceModel.fromResource(resource)
	    ));
	    this.relationsView.get(0).detectChanges();
	}

	async loadReferencesComponent() {
	    if (!this.relationsTab.expanded) {
	        return;
	    }
	    const { VisualizerModule } = await import('../../../../extension/visualizer/visualizer.module');
	    const moduleRef = createNgModuleRef(VisualizerModule, this.injector);
	    if (this.relationsComponent) {
	        this.relationsComponent.destroy();
	    }
	    if (this.relationsComponentInstance) {
	        this.relationsComponentInstance.destroy();
	    }
	    this.relationsComponent = this.relationsView.createComponent(VisualizerModule.components.relationsComponent, { ngModuleRef: moduleRef });
	    this.relationsComponentInstance = this.relationsComponent.instance;
	    this.relationsView.get(0).detectChanges();
	}

	async loadRTDResources(resource: ResourceModel) {
	    let rtdResources;
	    if (!this?.resourcesTab?.expanded) {
	        return;
	    }
	    if (this.validatePrincipalsPermissions(resource)) {


	        if (Utils.getState(resource, false) !== ResourceState.READY) {
	            this.rtdResourcesStateMessage = true;
	        }
	        const rtdTypesArrays = resource?.resource?.spec?.resources?.map((res: { type: any; }) => res.type);
	        this.hasPrivilegesPermissions = true;
	        this.loadingRTDResources = true;
	        try{
	            rtdResources = await firstValueFrom(this.resourceDataService.getResourcesInPath(resource.path, `validateParent=false&group=${resource?.resource?.spec?.group}&type=${rtdTypesArrays}&recursive=true&isRoot=true`));
	        } catch (err : any) {
	            rtdResources = [];
	            this.rtdErrorDetails = err?.message;
	        }
	        this.loadingRTDResources = false;
	        this.rtdResources = rtdResources;

	    } else {
	        this.hasPrivilegesPermissions = false;
	    }
	}

	validatePrincipalsPermissions(resource: ResourceModel) {
	    let principals: [{subject : {name: string, type: string}}];
	    principals = resource?.resource?.spec?.authorization?.principals;
	    let userEmail = this._appConfigService.getUserInfoModel()?.email;
	    const userExist = principals?.find(principal => principal.subject.name === userEmail && principal.subject.type === 'User');
	    return userExist !== undefined;
	}

	async loadEvents(resource: ResourceModel) {
	    if (!this.eventsTab.expanded) {
	        return;
	    }
	    if (this.eventsComponentInstance && this.eventsComponentInstance.resourceModel?.uid === resource?.uid) {
	        this.eventsComponentInstance.activate();
	        return;
	    }
	    const { EventsModule } = await import('../../../../extension/events/events.module');
	    const moduleRef = createNgModuleRef(EventsModule, this.injector);
	    if (this.eventsComponentRef) {
	        this.eventsComponentRef.destroy();
	    }
	    if (this.eventsComponentInstance) {
	        this.eventsComponentInstance.destroy();
	    }
	    this.eventsComponentRef = this.eventsView.createComponent(EventsModule.components.eventsViewerComponent, { ngModuleRef: moduleRef });
	    this.eventsComponentInstance = this.eventsComponentRef.instance;
	    this.eventsComponentInstance.resourceModel = resource;
	    this.eventsComponentInstance.enableFullScreen = true;
	    this._ngZone.run(() => { this.eventsComponentInstance.loadData(); });
	}

	ngAfterViewInit() {
		 this.loadReferencesComponent();
		 this.loadAutomationComponent(false);
	}

	isAutomation() {
	    return  this.isAutomationSolution;
	}

	async loadAutomationComponent(invalidSolution: boolean, invalidSolutionMessage: string = '') {
	    if (!this.automationTab?.expanded) {
	        return;
	    }
	    const { CloudAutomationDesignerModule } = await import('../../../../extension/cloud-automation-designer/cloud-automation-designer.module');
	    const moduleRef = createNgModuleRef(CloudAutomationDesignerModule, this.injector);
	    if (this.automationComponent) {
	        this.automationComponent.destroy();
	    }

	    this.automationComponent = this.automationView.createComponent(CloudAutomationDesignerModule.components.miniDesignerComponent, { ngModuleRef: moduleRef });
	    this.automationComponentInstance = this.automationComponent.instance;
	    this.automationComponentInstance.invalidSolution = invalidSolution;
	    this.automationComponentInstance.invalidSolutionMessage = invalidSolutionMessage;
	    this.automationComponentInstance.readySolutionResource = this.resource.state === ResourceState.READY;
	    this.automationView.get(0).detectChanges();
	    this.solutionService = this.injector.get(CloudAutomationDesignerModule.services.solutionService);
	}

	readonly featureToggles = Const;
}
